// components/UploadCard.js - File upload card component
import React, { useCallback, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import { FaCloudUploadAlt, FaImage, FaFileUpload } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { cn } from '../lib/utils';


const UploadCard = forwardRef(({
  
  title = 'Upload File',
  description = 'Drag and drop your file here, or click to browse',
  onUpload,
  onFileAccepted,
  acceptedFileTypes = 'image/jpeg,image/png',
  maxSize = 10 * 1024 * 1024, // 10MB default
  icon: Icon = FaCloudUploadAlt,
  hint = 'common.uploadHint'
}, ref) => {
  const { t } = useTranslation();
  const translatedHint = t(hint);
  console.log('UploadCard rendered with acceptedFileTypes:', acceptedFileTypes);
  
  // Parse the acceptedFileTypes string into an object for useDropzone
  const acceptedTypesArray = acceptedFileTypes.split(',').map(type => type.trim());
  console.log('Accepted types array:', acceptedTypesArray);
  
  // Create an object for useDropzone accept prop
  const acceptObj = {};
  acceptedTypesArray.forEach(type => {
    switch(type) {
      case 'image/jpeg':
        acceptObj[type] = ['.jpg', '.jpeg'];
        break;
      case 'image/png':
        acceptObj[type] = ['.png'];
        break;
      case 'image/webp':
        acceptObj[type] = ['.webp'];
        break;
      default:
        acceptObj[type] = [];
    }
  });
  
  console.log('Parsed accept object for dropzone:', acceptObj);

  const onDrop = useCallback(acceptedFiles => {
    console.log('UploadCard onDrop called with files:', acceptedFiles);
    if (acceptedFiles && acceptedFiles.length > 0) {
      // Use onFileAccepted if provided, otherwise fall back to onUpload
      const uploadHandler = onFileAccepted || onUpload;
      console.log('Using upload handler:', uploadHandler ? 'Available' : 'Not available');
      if (typeof uploadHandler === 'function') {
        console.log('Calling upload handler with file:', acceptedFiles[0]);
        uploadHandler(acceptedFiles[0]);
      } else {
        console.error('Upload handler is not a function:', uploadHandler);
      }
    } else {
      console.warn('No accepted files in onDrop');
    }
  }, [onUpload, onFileAccepted]);

  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject, open } = useDropzone({
    onDrop,
    accept: acceptObj,
    maxSize,
    multiple: false
  });

  // Expose the open method through the ref
  React.useImperativeHandle(ref, () => ({
    click: open
  }));

  // Animation variants
  const containerVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.3 } },
    hover: { scale: 1.02, transition: { type: "spring", stiffness: 400, damping: 10 } }
  };

  const iconVariants = {
    initial: { scale: 1 },
    hover: { scale: 1.1, rotate: 5, transition: { repeat: Infinity, repeatType: "reverse", duration: 1.5 } },
    dragActive: { scale: 1.2, rotate: 0, y: -5, transition: { duration: 0.3 } }
  };

  return (
    <motion.div
      initial="initial"
      animate="animate"
      whileHover="hover"
      variants={containerVariants}
      className="w-full"
    >
      <div 
        {...getRootProps()} 
        className={cn(
          "glass rounded-xl border-2 border-dashed p-8 text-center transition-all cursor-pointer",
          "hover:shadow-md hover:border-primary hover:border-opacity-50 dark:hover:border-primary-light dark:hover:border-opacity-50",
          isDragActive && !isDragReject && "border-primary border-opacity-70 bg-primary bg-opacity-5 shadow-md dark:border-primary-light dark:border-opacity-70 dark:bg-primary-dark dark:bg-opacity-10",
          isDragReject && "border-error border-opacity-70 bg-error bg-opacity-5 dark:border-error dark:border-opacity-70 dark:bg-error dark:bg-opacity-10",
          isDragAccept && "border-success border-opacity-70 bg-success bg-opacity-5 dark:border-success dark:border-opacity-70 dark:bg-success dark:bg-opacity-10"
        )}
      >
        <input {...getInputProps()} className="hidden" />
        
        <motion.div 
          className="flex flex-col items-center justify-center"
          variants={{
            initial: { opacity: 1 },
            hover: { opacity: 1 },
            dragActive: { opacity: 1, scale: 1.05 }
          }}
          animate={isDragActive ? "dragActive" : "initial"}
        >
          <motion.div
            className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-4 dark:bg-primary-dark/20"
            variants={iconVariants}
          >
            {isDragActive ? (
              <FaFileUpload className="text-icon-primary text-3xl" />
            ) : isDragAccept ? (
              <FaImage className="text-success text-3xl" />
            ) : isDragReject ? (
              <FaImage className="text-error text-3xl" />
            ) : (
              <Icon className="text-icon-primary text-3xl" />
            )}
          </motion.div>
          
          <motion.h3 
            className="text-xl font-semibold mb-2 text-text"
            variants={{
              initial: { y: 0 },
              hover: { y: -2 },
              dragActive: { y: -5 }
            }}
          >
            {title}
          </motion.h3>
          
          <motion.p 
            className="text-text-light mb-4"
            variants={{
              initial: { y: 0 },
              hover: { y: -2 },
              dragActive: { y: -5, opacity: 0.7 }
            }}
          >
            {isDragActive ? 'Drop the file here...' : description}
          </motion.p>
          
          <motion.div
            className="text-sm text-text-light opacity-70"
            variants={{
              initial: { opacity: 0.7 },
              hover: { opacity: 1 },
              dragActive: { opacity: 0 }
            }}
          >
            {translatedHint}
          </motion.div>
        </motion.div>
      </div>
    </motion.div>
  );
});

UploadCard.displayName = 'UploadCard';

export default UploadCard;
